// The PDP's pincode field and its result states. Transport, error
// classification and delivery-date handling live in serviceability-api.js,
// shared with the cart drawer's per-line delivery rows — two copies of the date
// logic is how the same product ends up carrying two different promises.
//
// An unserviceable pincode is NOT an error: a real-but-undeliverable pincode
// comes back as success with serviceable:false (handled in renderResult). Only a
// malformed one errors, with "Invalid pincode".
// Verdicts per (pincode, sku), read by the add-to-cart gate so a second click does
// not re-ask. A manual submit always re-requests — the shopper asked for it.
// Module scope: Quick View can rebuild this element under the same product.
const PINCODE_VERDICTS = new Map();

// Back/forward restore replays the JS heap; delivery answers go stale by the day.
window.addEventListener("pageshow", (event) => {
  if (event.persisted) PINCODE_VERDICTS.clear();
});

class PincodeCheck extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("[data-pincode-form]");
    this.input = this.querySelector("[data-pincode-input]");
    this.submitButton = this.querySelector("[data-pincode-submit]");
    this.errorEl = this.querySelector("[data-pincode-error]");
    this.resultEl = this.querySelector("[data-pincode-result]");
    this.deliveryTextEl = this.querySelector("[data-pincode-delivery-text]");
    this.codEl = this.querySelector("[data-pincode-cod]");
    this.unavailableEl = this.querySelector("[data-pincode-unavailable]");
    this.noItemEl = this.querySelector("[data-pincode-no-item]");
    this.networkErrorEl = this.querySelector("[data-pincode-network-error]");
    this.promptEl = this.querySelector("[data-pincode-prompt]");
    if (!this.form || !this.input) return;

    const { endpoint, brandSlug, deliveryPrefix, productId } = this.dataset;
    this.endpoint = endpoint;
    this.brandSlug = brandSlug;
    this.deliveryPrefix = deliveryPrefix || "Delivery by";
    this.productId = productId;
    this.length = parseInt(this.dataset.length, 10) || 6;
    // Fallback SKU for single-variant products (no variant selector on page).
    this.currentSku = this.dataset.sku || "";
    this.variantSelector =
      customElements
        .get("variant-selector")
        ?.nearest?.(this, this.productId) ?? null;

    this.onSubmit = (event) => this.handleSubmit(event);
    this.onInput = () => this.sanitize();
    this.form.addEventListener("submit", this.onSubmit);
    this.input.addEventListener("input", this.onInput);

    // Pre-fill the last pincode this shopper checked, so a second PDP does not
    // ask for it again. Deliberately does NOT auto-run the check: that would put
    // a serviceability request on every PDP view, and the shopper is not always
    // here for delivery. They still press the button; they just don't retype.
    //
    // Optional-chained because pincode-storage.js loads from core-assets in the
    // head while this loads from the product section — document order makes that
    // safe today, and this degrades to "no pre-fill" rather than throwing if it
    // ever stops being true.
    const rememberedPincode = window.PincodeStorage?.get() ?? "";
    if (rememberedPincode && !this.input.value) {
      this.input.value = rememberedPincode;
    }
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.onSubmit);
    this.input?.removeEventListener("input", this.onInput);
  }

  sanitize() {
    const digitsOnly = this.input.value
      .replace(/\D/g, "")
      .slice(0, this.length);
    if (digitsOnly !== this.input.value) this.input.value = digitsOnly;
  }

  hideAllStates() {
    this.errorEl?.setAttribute("hidden", "");
    this.resultEl?.setAttribute("hidden", "");
    this.unavailableEl?.setAttribute("hidden", "");
    this.noItemEl?.setAttribute("hidden", "");
    this.networkErrorEl?.setAttribute("hidden", "");
    this.promptEl?.setAttribute("hidden", "");
  }

  async handleSubmit(event) {
    event.preventDefault();
    this.sanitize();

    const pincode = this.input.value.trim();
    const isValid = new RegExp(`^\\d{${this.length}}$`).test(pincode);

    this.hideAllStates();

    if (!isValid) {
      this.errorEl?.removeAttribute("hidden");
      return;
    }

    const sku = this.resolveSku();
    // resolveSku handles its own UI (shake + prompt, or missing-SKU error).
    if (!sku) return;

    await this.check(pincode, sku);
  }

  // Runs the check and renders this block's result state. Returns the verdict:
  // true deliverable, false refused, null when the question could not be asked.
  //
  // A successful response with no row for the SKU counts as REFUSED, not unknown —
  // renderResult already tells the shopper we do not deliver it, and a gate that
  // let the add through anyway would contradict the block right above it. Only a
  // thrown request is unknown, and unknown never blocks.
  async check(pincode, sku) {
    if (!this.endpoint) {
      this.showState("network");
      return null;
    }

    this.setLoading(true);
    try {
      const result = await this.checkServiceability(pincode, sku);
      // Remembered only once the backend has accepted the pincode — including
      // when it answers serviceable:false, which is a real location we simply do
      // not deliver to yet. A malformed pincode throws above and is never stored.
      window.PincodeStorage?.set(pincode);
      this.renderResult(result);

      const verdict = result ? result.serviceable !== false : false;
      PINCODE_VERDICTS.set(`${pincode}|${sku}`, verdict);
      document.dispatchEvent(
        new CustomEvent("pincode:checked", {
          detail: { productId: this.productId, pincode, serviceable: verdict },
        }),
      );
      return verdict;
    } catch (error) {
      console.error("[pincode-check] serviceability check failed:", error);
      this.showState(error.state ?? "network");
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  // Public. The add-to-cart gate's entry point: true when the add may proceed.
  // A shopper with no pincode is prompted here rather than stopped silently —
  // this block is the prompt, which is why there is no modal.
  async ensureDeliverable(sku) {
    // What is in the field outranks what was remembered.
    //
    // This used to read storage alone, and storage is only written inside check() — which
    // runs from this block's own arrow button and nowhere else. So a pincode typed into the
    // field but never submitted through that button was invisible here: pressing the buy
    // button (reading "Check Delivery" by then) re-prompted for a pincode already on screen,
    // and the arrow was the only way forward. The buy button now answers its own prompt.
    this.sanitize();
    const typed = this.input?.value.trim() ?? "";
    const stored = window.PincodeStorage?.get() ?? "";

    let pincode = stored;
    if (typed && typed !== stored) {
      // Half-typed is an answer to the prompt, just not a usable one. Say so against the
      // field — quietly checking the remembered pincode instead would answer a question the
      // shopper is visibly in the middle of changing.
      if (!new RegExp(`^\\d{${this.length}}$`).test(typed)) {
        this.hideAllStates();
        this.errorEl?.removeAttribute("hidden");
        this.drawAttention();
        return false;
      }
      pincode = typed;
    }

    if (!pincode) {
      this.promptForPincode();
      return false;
    }

    if (this.input && !this.input.value) this.input.value = pincode;

    const cached = PINCODE_VERDICTS.get(`${pincode}|${sku}`);
    if (cached === false) {
      this.hideAllStates();
      this.showState("unavailable");
      this.drawAttention();
      return false;
    }
    if (cached === true) return true;

    this.hideAllStates();
    const verdict = await this.check(pincode, sku);
    if (verdict === false) {
      // check() has already rendered the refusal; just bring it into view.
      this.drawAttention();
      return false;
    }
    // true, and null (unaskable) — never block on a question we failed to put.
    return true;
  }

  // No pincode yet, so the block has nothing else to say: state why it just took
  // the shopper's attention, then draw the eye.
  //
  // Deliberately NOT used for a refusal. The block is already showing "we do not
  // deliver to this pincode yet", and stacking "check delivery to add this item"
  // under it reads as if they had not just done exactly that.
  promptForPincode() {
    this.promptEl?.removeAttribute("hidden");
    this.drawAttention();
  }

  // Mirrors variant-selector's shakeMissingOptions: a one-shot shake, and a scroll
  // only when the field is actually off screen so the page does not jerk on every
  // click. Says nothing itself — the caller owns the message.
  drawAttention() {
    if (this.form) {
      this.form.classList.remove("animate-shake");
      // Force reflow so re-adding the class restarts the animation on repeat clicks.
      void this.form.offsetWidth;
      this.form.classList.add("animate-shake");
      this.form.addEventListener(
        "animationend",
        () => this.form.classList.remove("animate-shake"),
        { once: true },
      );
    }

    const rect = this.getBoundingClientRect();
    if (rect.top < 0 || rect.bottom > window.innerHeight) {
      this.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    this.input?.focus({ preventScroll: true });
  }

  showState(state) {
    if (state === "invalid") {
      this.errorEl?.removeAttribute("hidden");
      return;
    }
    if (state === "unavailable") {
      this.unavailableEl?.removeAttribute("hidden");
      return;
    }
    if (state === "no-item") {
      this.noItemEl?.removeAttribute("hidden");
      return;
    }
    this.networkErrorEl?.removeAttribute("hidden");
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;
    this.submitButton.setAttribute("aria-busy", isLoading ? "true" : "false");
  }

  // Returns the SKU to check, or null after handling the UI itself: prompts on the
  // option group (e.g. size) when no variant is chosen, or shows the error state
  // when the selected variant has no SKU.
  //
  // The prompt lives on the option group alone. A second copy under the pincode
  // field said the same thing twice on one screen.
  resolveSku() {
    if (this.variantSelector) {
      const variant = this.variantSelector.getSelectedVariant?.();
      if (!variant) {
        this.variantSelector.shakeMissingOptions?.();
        return null;
      }
      if (!variant.sku) {
        // Data gap, not a request failure: the API keys on the variant's SKU and
        // Shopify has none for this variant, so delivery is uncheckable.
        console.warn(
          "[pincode-check] selected variant has no SKU — cannot check serviceability",
          variant,
        );
        this.showState("no-item");
        return null;
      }
      return variant.sku;
    }

    // Single-variant product: use the SKU rendered server-side.
    if (!this.currentSku) {
      console.warn(
        "[pincode-check] no SKU on product — cannot check serviceability",
      );
      this.showState("no-item");
      return null;
    }
    return this.currentSku;
  }

  async checkServiceability(pincode, sku) {
    const list = await window.ServiceabilityApi.check({
      endpoint: this.endpoint,
      brandSlug: this.brandSlug,
      pincode,
      items: [{ skuId: sku, quantity: 1 }],
    });

    // One result per requested item; the PDP asks about exactly one.
    return list.find((item) => item.skuId === sku) ?? list[0] ?? null;
  }

  renderResult(result) {
    // The request succeeded but carried no row for this SKU. The backend
    // answered authoritatively, so treat it as a coverage answer rather than a
    // transport failure — "try again" would just repeat the same empty result.
    if (!result) {
      this.unavailableEl?.removeAttribute("hidden");
      return;
    }

    if (!result.serviceable) {
      this.unavailableEl?.removeAttribute("hidden");
      return;
    }

    if (this.deliveryTextEl) {
      this.deliveryTextEl.textContent = this.buildDeliveryText(
        result.deliveryOptions,
      );
    }
    this.codEl?.toggleAttribute("hidden", !result.paymentMethods?.codEnabled);
    this.resultEl?.removeAttribute("hidden");
  }

  buildDeliveryText(deliveryOptions) {
    const api = window.ServiceabilityApi;
    const soonest = api?.soonestDeliveryTime(deliveryOptions) ?? null;
    if (soonest !== null) {
      return `${this.deliveryPrefix} ${api.formatDeliveryDate(soonest)}`;
    }

    // Couldn't parse a date — show the raw value rather than nothing.
    const rawDate = (deliveryOptions ?? [])
      .map((option) => option.expectedDeliveryDate)
      .find(Boolean);
    if (rawDate) return `${this.deliveryPrefix} ${rawDate}`;

    return "Deliverable to your pincode.";
  }
}

if (!customElements.get("pincode-check")) {
  customElements.define("pincode-check", PincodeCheck);
}
